import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { useUser } from './UserContext';

// Define interfaces for prerequisite data
interface PrerequisiteCourse {
  CourseCode: string;
  MinGrade: string;
  CourseName?: string;
  RequiredInAverageCalc?: string;
}

interface PrerequisiteGroup {
  CourseList: { [courseCode: string]: string }; // Firebase stores course codes as keys with MinGrade as string values
  MinCountRequired: string;
  GroupName?: string;
  MinGrade?: string;
  Rank?: string;
  RequiredInAverageCalc?: string;
  Type?: string;
}

interface Prerequisite {
  ProgramID: string;
  ProgramName: string;
  SchoolName: string;
  ProgramAverage: string;
  ProgramAvgType: string;
  PrerequesiteCount: string;
  IsEngRequired: string;
  LastUpdated: string;
  CourseRequiredInAverageCalcList?: { [courseCode: string]: string };
  PrerequisiteList: { [groupId: string]: PrerequisiteGroup };
}

interface PrerequisiteContextType {
  prerequisites: { [prerequisiteId: string]: Prerequisite };
  loading: boolean;
  error: string | null;
  fetchPrerequisites: () => Promise<void>;
  getPrerequisitesForProgram: (programId: string) => Prerequisite | null;
  getPrerequisitesForProgramCode: (programCode: string) => Prerequisite | null;
}

const PrerequisiteContext = createContext<PrerequisiteContextType | undefined>(undefined);

export const usePrerequisites = () => {
  const context = useContext(PrerequisiteContext);
  if (context === undefined) {
    throw new Error('usePrerequisites must be used within a PrerequisiteProvider');
  }
  return context;
};

interface PrerequisiteProviderProps {
  children: ReactNode;
}

export const PrerequisiteProvider: React.FC<PrerequisiteProviderProps> = ({ children }) => {
  const [prerequisites, setPrerequisites] = useState<{ [prerequisiteId: string]: Prerequisite }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const fetchPrerequisites = async () => {
    // Don't fetch if user is not authenticated
    if (!user) {
      setLoading(false);
      setPrerequisites({});
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching prerequisites from Firebase...');
      const db = getDatabase();
      const prerequisitesRef = ref(db, 'prerequisite');
      const snapshot = await get(prerequisitesRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('Prerequisites fetched:', Object.keys(data).length, 'entries');
        setPrerequisites(data);
      } else {
        console.log('No prerequisites found in database');
        setPrerequisites({});
      }
    } catch (err) {
      console.error('Error fetching prerequisites:', err);
      setError('Failed to fetch prerequisites');
    } finally {
      setLoading(false);
    }
  };

  const getPrerequisitesForProgram = (programId: string): Prerequisite | null => {
    // Find prerequisite by ProgramID
    for (const [prerequisiteId, prerequisite] of Object.entries(prerequisites)) {
      if (prerequisite.ProgramID === programId) {
        return prerequisite;
      }
    }
    return null;
  };

  const getPrerequisitesForProgramCode = (programCode: string): Prerequisite | null => {
    // Find prerequisite by ProgramName (which might contain the program code)
    for (const [prerequisiteId, prerequisite] of Object.entries(prerequisites)) {
      if (prerequisite.ProgramName && prerequisite.ProgramName.includes(programCode)) {
        return prerequisite;
      }
    }
    return null;
  };

  useEffect(() => {
    fetchPrerequisites();
  }, [user]);

  const value: PrerequisiteContextType = {
    prerequisites,
    loading,
    error,
    fetchPrerequisites,
    getPrerequisitesForProgram,
    getPrerequisitesForProgramCode,
  };

  return (
    <PrerequisiteContext.Provider value={value}>
      {children}
    </PrerequisiteContext.Provider>
  );
};
